import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useInquiry } from '../../../hooks/useInquiry';
import { useSearch } from '../../../hooks/useSearch';
import { useAuth } from '../../../hooks/useAuth';
import { InquiryStatus, InquiryCategory, InquiryPriority } from '../../../types/inquiry';

const InquiryManagePage: React.FC = () => {
  const { user } = useAuth();
  const { inquiries, loading, error, statistics, hasUnreadMessages, refreshInquiries } = useInquiry({
    adminMode: true,
    realtime: true
  });

  const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<InquiryCategory | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<InquiryPriority | 'all'>('all');

  // 실시간 알림 상태
  const [newInquiryCount, setNewInquiryCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connecting');
  const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());

  // 이전 문의 개수 추적 (새 문의 알림용)
  const [prevInquiryCount, setPrevInquiryCount] = useState(0);

  // 검색 기능
  const searchFields = [
    { path: 'subject' },
    { path: 'content' },
    { path: 'userInfo.name' },
    { path: 'userInfo.organization' }
  ];

  const {
    searchTerm,
    filteredItems: filteredInquiries,
    handleSearchChange
  } = useSearch(inquiries, searchFields);

  // 실시간 업데이트 감지
  useEffect(() => {
    if (!loading && inquiries.length > 0) {
      setConnectionStatus('connected');
      setLastUpdateTime(new Date());

      // 새 문의가 추가되었는지 확인
      if (prevInquiryCount > 0 && inquiries.length > prevInquiryCount) {
        const newCount = inquiries.length - prevInquiryCount;
        setNewInquiryCount(prev => prev + newCount);
      }

      setPrevInquiryCount(inquiries.length);
    } else if (loading) {
      setConnectionStatus('connecting');
    }
  }, [inquiries, loading, prevInquiryCount]);

  // 연결 상태 모니터링
  useEffect(() => {
    if (error) {
      setConnectionStatus('disconnected');
    }
  }, [error]);


  // 새 문의 알림 클리어
  const clearNewInquiryNotification = useCallback(() => {
    setNewInquiryCount(0);
  }, []);

  // 수동 새로고침
  const handleRefresh = useCallback(() => {
    setConnectionStatus('connecting');
    refreshInquiries();
  }, [refreshInquiries]);

  // 필터링된 문의들
  const displayInquiries = useMemo(() => {
    let result = filteredInquiries;

    if (statusFilter !== 'all') {
      result = result.filter(inquiry => inquiry.status === statusFilter);
    }

    if (categoryFilter !== 'all') {
      result = result.filter(inquiry => inquiry.category === categoryFilter);
    }

    if (priorityFilter !== 'all') {
      result = result.filter(inquiry => inquiry.priority === priorityFilter);
    }

    return result.sort((a, b) => {
      // 우선순위별 정렬 (high > normal > low)
      const priorityOrder = { high: 3, normal: 2, low: 1 };
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];

      if (priorityDiff !== 0) return priorityDiff;

      // 상태별 정렬 (pending > answered > closed)
      const statusOrder = { pending: 3, answered: 2, closed: 1 };
      const statusDiff = statusOrder[b.status] - statusOrder[a.status];

      if (statusDiff !== 0) return statusDiff;

      // 최신 순
      return b.createdAt.getTime() - a.createdAt.getTime();
    });
  }, [filteredInquiries, statusFilter, categoryFilter, priorityFilter]);

  // 라벨 변환 함수들
  const getStatusLabel = (status: InquiryStatus): string => {
    switch (status) {
      case 'pending': return '답변대기';
      case 'answered': return '답변완료';
      case 'closed': return '문의완료';
      default: return status;
    }
  };

  const getCategoryLabel = (category: InquiryCategory): string => {
    switch (category) {
      case 'general': return '일반문의';
      case 'portville': return '포트빌';
      case 'parking': return '주차장';
      case 'facility': return '시설물';
      case 'account': return '계정';
      default: return category;
    }
  };

  const getPriorityLabel = (priority: InquiryPriority): string => {
    switch (priority) {
      case 'high': return '긴급';
      case 'normal': return '보통';
      case 'low': return '낮음';
      default: return priority;
    }
  };

  // 색상 클래스 함수들
  const getStatusColor = (status: InquiryStatus): string => {
    switch (status) {
      case 'pending': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'answered': return 'text-green-600 bg-green-50 border-green-200';
      case 'closed': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPriorityColor = (priority: InquiryPriority): string => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'normal': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // 날짜 포맷팅
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 font-medium">오류가 발생했습니다</div>
          <div className="text-red-600 text-sm mt-1">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <h1 className="text-3xl font-bold text-gray-900 mr-4">고객센터 관리</h1>

            {/* 실시간 연결 상태 */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-2 ${
                  connectionStatus === 'connected' ? 'bg-green-500 animate-pulse' :
                  connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                  'bg-red-500'
                }`}></div>
                <span className={`text-sm font-medium ${
                  connectionStatus === 'connected' ? 'text-green-700' :
                  connectionStatus === 'connecting' ? 'text-yellow-700' :
                  'text-red-700'
                }`}>
                  {connectionStatus === 'connected' ? '실시간 연결됨' :
                   connectionStatus === 'connecting' ? '연결 중...' :
                   '연결 끊어짐'}
                </span>
              </div>

              {/* 새 문의 알림 */}
              {newInquiryCount > 0 && (
                <div className="flex items-center">
                  <div className="bg-red-100 border border-red-200 rounded-lg px-3 py-1 flex items-center">
                    <svg className="w-4 h-4 text-red-600 mr-2 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5-5-5h5v-12a9 9 0 10-9 9 9 9 0 009-9z" />
                    </svg>
                    <span className="text-red-800 font-medium text-sm">새 문의 {newInquiryCount}건</span>
                    <button
                      onClick={clearNewInquiryNotification}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

              {/* 마지막 업데이트 시간 */}
              <div className="text-xs text-gray-500">
                마지막 업데이트: {lastUpdateTime.toLocaleTimeString()}
              </div>
            </div>
          </div>

          {/* 컨트롤 버튼들 */}
          <div className="flex items-center space-x-2">

            {/* 새로고침 버튼 */}
            <button
              onClick={handleRefresh}
              disabled={connectionStatus === 'connecting'}
              className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="새로고침"
            >
              <svg className={`w-5 h-5 ${connectionStatus === 'connecting' ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* 통계 카드 */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">전체 문의</dt>
                      <dd className="text-lg font-medium text-gray-900">{statistics.total}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">답변대기</dt>
                      <dd className="text-lg font-medium text-gray-900">{statistics.pending}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">답변완료</dt>
                      <dd className="text-lg font-medium text-gray-900">{statistics.answered}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-gray-500 rounded-md flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">문의완료</dt>
                      <dd className="text-lg font-medium text-gray-900">{statistics.closed}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 필터 및 검색 */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* 검색 */}
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="제목, 내용, 작성자로 검색..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* 필터 */}
            <div className="flex flex-wrap gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as InquiryStatus | 'all')}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">모든 상태</option>
                <option value="pending">답변대기</option>
                <option value="answered">답변완료</option>
                <option value="closed">문의완료</option>
              </select>

              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as InquiryCategory | 'all')}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">모든 카테고리</option>
                <option value="general">일반문의</option>
                <option value="portville">포트빌</option>
                <option value="parking">주차장</option>
                <option value="facility">시설물</option>
                <option value="account">계정</option>
              </select>

              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as InquiryPriority | 'all')}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">모든 우선순위</option>
                <option value="high">긴급</option>
                <option value="normal">보통</option>
                <option value="low">낮음</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* 문의 목록 */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">문의 목록을 불러오는 중...</p>
          </div>
        ) : displayInquiries.length === 0 ? (
          <div className="p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">문의가 없습니다</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' || priorityFilter !== 'all'
                ? '검색 조건에 맞는 문의가 없습니다.'
                : '등록된 문의가 없습니다.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {displayInquiries.map((inquiry) => {
              // 실제 관리자 UID를 사용하여 새 메시지 여부 확인
              const hasUnread = user ? hasUnreadMessages(inquiry, user.uid) : false;

              return (
                <li key={inquiry.id}>
                  <Link
                    to={`/admin/inquiry/${inquiry.id}`}
                    className={`block hover:bg-gray-50 px-6 py-4 ${
                      hasUnread ? 'bg-yellow-50 border-l-4 border-yellow-400' : ''
                    }`}
                  >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        {hasUnread && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full border border-red-200 animate-pulse">
                            <svg className="w-3 h-3 mr-1 animate-bounce" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            새 메시지
                          </span>
                        )}

                        {/* 최근 생성된 문의 표시 (1시간 이내) */}
                        {(() => {
                          const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
                          const isRecent = inquiry.createdAt > hourAgo;
                          return isRecent && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full border border-green-200">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              NEW
                            </span>
                          );
                        })()}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(inquiry.priority)}`}>
                          {getPriorityLabel(inquiry.priority)}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(inquiry.status)}`}>
                          {getStatusLabel(inquiry.status)}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-800 bg-gray-100">
                          {getCategoryLabel(inquiry.category)}
                        </span>
                      </div>

                      <p className="text-lg font-medium text-gray-900 truncate mb-1">
                        {inquiry.subject}
                      </p>

                      <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                        {inquiry.content}
                      </p>

                      {/* 최신 메시지 2~3개 표시 */}
                      {inquiry.messages && inquiry.messages.length > 0 && (
                        <div className="mt-2 mb-2 space-y-1">
                          {inquiry.messages
                            .slice(-3)
                            .reverse()
                            .map((message, index) => (
                              <div
                                key={index}
                                className={`text-xs px-2 py-1 rounded ${
                                  message.senderType === 'admin'
                                    ? 'bg-blue-50 text-blue-800 border border-blue-200'
                                    : 'bg-gray-50 text-gray-700 border border-gray-200'
                                }`}
                              >
                                <span className="font-medium">
                                  {message.senderType === 'admin' ? '관리자' : '문의자'}:
                                </span>{' '}
                                <span className="line-clamp-1">{message.content}</span>
                                <span className="text-gray-500 ml-1">
                                  ({formatDate(message.createdAt)})
                                </span>
                              </div>
                            ))}
                        </div>
                      )}

                      <div className="flex items-center text-sm text-gray-500 gap-4">
                        <span className="flex items-center">
                          <svg className="flex-shrink-0 mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {inquiry.userInfo.name}
                        </span>

                        <span className="flex items-center">
                          <svg className="flex-shrink-0 mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h1m-1-4h1m4 4h1m-1-4h1" />
                          </svg>
                          {inquiry.userInfo.organization}
                        </span>

                        <span className="flex items-center">
                          <svg className="flex-shrink-0 mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {formatDate(inquiry.createdAt)}
                        </span>

                        {inquiry.attachments && inquiry.attachments.length > 0 && (
                          <span className="flex items-center text-blue-600">
                            <svg className="flex-shrink-0 mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            첨부 {inquiry.attachments.length}개
                          </span>
                        )}

                        {inquiry.adminResponse && (
                          <span className="text-green-600 font-medium">
                            관리자 답변완료
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="ml-4 flex-shrink-0">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default InquiryManagePage;